import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Container, Row, Col } from "react-bootstrap";
import { LayoutOne } from "@/layouts";
import SeoHead from "@/components/SeoHead";
import PageViewBeacon from "@/components/PageViewBeacon";
import ProductItem from "@/components/product";
import OurGroupSection from "@/components/ourGroup";
import { getAllProjects, isProjectsEnabled } from "@/lib/projects";
import { fetchGlobalSettings, getGlobalSetting } from "@/lib/globalSettings";
import { PROJECTS_LIST_SCHEMA } from "@/lib/projectsListSchema";

function Projects({ projects = [], bannerTitle, seoTitle, seoDescription }) {
  const [activeStage, setActiveStage] = useState("all");
  const { cartItems } = useSelector((state) => state.cart);
  const { wishlistItems } = useSelector((state) => state.wishlist);
  const { compareItems } = useSelector((state) => state.compare);
  const projectTabs = [
    { value: "all", label: "All Projects" },
    { value: "ongoing", label: "Ongoing" },
    { value: "upcoming", label: "Upcoming" },
    { value: "completed", label: "Completed" },
  ];
  const projectCounts = useMemo(
    () =>
      projects.reduce(
        (counts, project) => {
          const stage = project.project_stage || "ongoing";
          counts.all += 1;
          if (Object.prototype.hasOwnProperty.call(counts, stage)) counts[stage] += 1;
          return counts;
        },
        { all: 0, ongoing: 0, upcoming: 0, completed: 0 }
      ),
    [projects]
  );
  const visibleProjects = useMemo(
    () =>
      activeStage === "all"
        ? projects
        : projects.filter((project) => project.project_stage === activeStage),
    [activeStage, projects]
  );

  return (
    <>
      <PageViewBeacon contentType="project_index" path="/projects" />
      <SeoHead
        path="/projects"
        fallbackTitle={seoTitle}
        fallbackDescription={seoDescription}
        publisherName="Siddhivinayak Group"
        seo={{
          seo_title: seoTitle,
          meta_description: seoDescription,
          focus_keyword: "premium residential projects Nagpur",
          secondary_keywords: [
            "Siddhivinayak Group projects",
            "2 BHK 3 BHK flats Nagpur",
            "RL plots Nagpur",
            "RERA approved projects",
            "Kunal Padole Group projects",
          ],
          og_image: "/img/og/siddhivinayak-group-og.jpg",
          schema_json: PROJECTS_LIST_SCHEMA,
        }}
        extraKeywords={["Hilltop Heights", "Ayodhya City", "Siddhivinayak Nagari", "Embassy"]}
      />
      <LayoutOne topbar={true} bannerTitle={bannerTitle}>

      <div className="ltn__product-area">
        <Container>
          <div className="projects-stage-tabs" role="tablist" aria-label="Filter projects by status">
            {projectTabs.map((tab) => (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={activeStage === tab.value}
                className={activeStage === tab.value ? "is-active" : ""}
                onClick={() => setActiveStage(tab.value)}
              >
                <span>{tab.label}</span>
                <span className="projects-stage-count" aria-label={`${projectCounts[tab.value]} projects`}>
                  {projectCounts[tab.value]}
                </span>
              </button>
            ))}
          </div>

          <Row>
            {visibleProjects.length > 0 ? (
              visibleProjects.map((product, key) => {
                const slug = product.slug || product.id;
                return (
                  <Col key={product.id || key} xs={12} sm={6} lg={4}>
                    <ProductItem
                      productData={product}
                      slug={slug}
                      baseUrl="projects"
                      cartItem={cartItems.find((c) => c.id === product.id)}
                      wishlistItem={wishlistItems.find((w) => w.id === product.id)}
                      compareItem={compareItems.find((c) => c.id === product.id)}
                      variant="featured"
                    />
                  </Col>
                );
              })
            ) : (
              <Col xs={12}>
                <p className="text-center py-5 mb-0">
                  {projects.length > 0
                    ? `No ${activeStage} projects available.`
                    : "No projects published yet. Create one in the admin."}
                </p>
              </Col>
            )}
          </Row>
        </Container>
      </div>

      <div className="section-bg-1">
        <OurGroupSection />
      </div>
      </LayoutOne>
    </>
  );
}

export default Projects;

export async function getStaticProps() {
  const enabled = isProjectsEnabled();
  if (!enabled) return { notFound: true, revalidate: 60 };
  const [projects, settings] = await Promise.all([
    getAllProjects(),
    fetchGlobalSettings(),
  ]);
  return {
    props: {
      projects,
      bannerTitle: getGlobalSetting(settings, "projects_banner_title", "Our Projects"),
      seoTitle: getGlobalSetting(
        settings,
        "projects_listing_seo_title",
        "Premium Residential Projects | Siddhivinayak Group"
      ),
      seoDescription: getGlobalSetting(
        settings,
        "projects_listing_seo_description",
        "Explore premium residential projects — apartments, villas, and gated communities across top Indian cities."
      ),
    },
    revalidate: 300,
  };
}
